import React from 'react';
import { Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

import type { MapMove, MapPing, HoveredPing } from '../types';
import type { ClickedStop } from '../overlays/createStopOverlays';
import { useMoveOverlays } from '../hooks/useMoveOverlays';
import PingTooltip from './PingTooltip';
import StopInfoPopup from './StopInfoPopup';

import { DEFAULT_US_BOUNDS } from '@utils/googleMapOptions';
import { AddressSearch, MapControls, MapNavigation, useMapPreferences } from '@components/Maps';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography, CircularProgress } from '@mui/material';

// TYPES --------------------------------------------------------------------- //

interface MoveMapViewProps {
  mapId: string;
  moves: MapMove[];
  pingsByMoveId: Record<number, MapPing[]>;
  unassignedPings: MapPing[];
  highlightedMoveId: number | null;
  moveColorMap: Record<number, string>;
  selectedDriverId: number | null;
  rooftopId?: number;
  loading?: boolean;
  defaultBounds?: google.maps.LatLngBoundsLiteral;
  defaultCenter?: google.maps.LatLngLiteral;
  defaultZoom?: number;
  showGeofences?: boolean;
  onHoverPing?: (ping: HoveredPing | null) => void;
  onMapReady?: (map: google.maps.Map) => void;
  onBoundsChanged?: (bounds: google.maps.LatLngBounds | null) => void;
  children?: React.ReactNode;
  topLeftOverlay?: React.ReactNode;
  topRightOverlay?: React.ReactNode;
  bottomOverlay?: React.ReactNode;
}

// COMPONENT ----------------------------------------------------------------- //

const MoveMapView = ({
  mapId,
  moves,
  pingsByMoveId,
  unassignedPings,
  highlightedMoveId,
  selectedDriverId,
  rooftopId,
  loading,
  showGeofences,
  defaultBounds,
  defaultCenter,
  defaultZoom,
  onHoverPing,
  onMapReady,
  children,
  topLeftOverlay,
  topRightOverlay,
  bottomOverlay,
}: MoveMapViewProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const { mapStyles: activeMapStyles } = useMapPreferences();

  const map = useMap(mapId);
  useMapsLibrary('maps');

  // Notify parent when map is ready
  React.useEffect(() => {
    if (map && onMapReady) onMapReady(map);
  }, [map, onMapReady]);

  // Hovered ping state
  const [hoveredPing, setHoveredPing] = React.useState<HoveredPing | null>(null);
  const handleHoverPing = React.useCallback(
    (ping: HoveredPing | null) => {
      setHoveredPing(ping);
      onHoverPing?.(ping);
    },
    [onHoverPing]
  );

  // Clicked stop state (pickup/delivery flag markers)
  const [clickedStop, setClickedStop] = React.useState<ClickedStop | null>(null);
  const handleClickStop = React.useCallback(
    (stop: ClickedStop | null) => setClickedStop(stop),
    []
  );
  const handleCloseStop = React.useCallback(() => setClickedStop(null), []);

  // Clear clicked stop when overlays rebuild (driver/move change)
  React.useEffect(() => {
    setClickedStop(null);
  }, [selectedDriverId, highlightedMoveId]);

  // Move detail overlays
  useMoveOverlays({
    map,
    moves,
    pingsByMoveId,
    unassignedPings,
    highlightedMoveId,
    selectedDriverId,
    showGeofences,
    onHoverPing: handleHoverPing,
    onClickStop: handleClickStop,
  });

  // Auto-satellite zoom: switch to hybrid at zoom 19+, revert on zoom out
  const autoSatelliteRef = React.useRef(false);

  React.useEffect(() => {
    if (!map) return;
    const listener = map.addListener('zoom_changed', () => {
      const zoom = map.getZoom() ?? 10;
      if (zoom >= 19 && !autoSatelliteRef.current) {
        autoSatelliteRef.current = true;
        map.setMapTypeId('hybrid');
      } else if (zoom < 19 && autoSatelliteRef.current) {
        autoSatelliteRef.current = false;
        map.setMapTypeId('roadmap');
      }
    });
    return () => google.maps.event.removeListener(listener);
  }, [map]);

  // Compute effective default bounds
  const effectiveBounds = defaultBounds || DEFAULT_US_BOUNDS;

  return (
    <div className={cls.mapContainer}>
      {/* Top-left overlay slot (selected driver panel) */}
      {topLeftOverlay}

      {/* Fly-to address search (top-left) */}
      <AddressSearch mapId={mapId} rooftopId={rooftopId} />

      {/* Top-right overlay slot (custom actions) */}
      {topRightOverlay ? <div className={cls.topRightOverlay}>{topRightOverlay}</div> : null}

      {/* Loading overlay */}
      {loading ? (
        <div className={cls.loadingOverlay}>
          <CircularProgress size={24} />
          <Typography variant='body2' sx={{ ml: 1, color: theme.palette.text.secondary }}>
            Loading route...
          </Typography>
        </div>
      ) : null}

      <Map
        id={mapId}
        defaultBounds={effectiveBounds}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        clickableIcons={false}
        gestureHandling='greedy'
        disableDefaultUI
        styles={activeMapStyles}
      >
        {children}
      </Map>

      {/* Shared map controls (fullscreen + light/dark + focus) */}
      <MapControls />

      {/* Shared zoom controls */}
      <MapNavigation mapId={mapId} />

      {/* Bottom overlay slot (warning/legend host) */}
      {bottomOverlay}

      {/* Ping hover tooltip */}
      {hoveredPing ? <PingTooltip ping={hoveredPing} /> : null}

      {/* Stop info popup (pickup/delivery click) */}
      {clickedStop ? <StopInfoPopup stop={clickedStop} onClose={handleCloseStop} /> : null}
    </div>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  mapContainer: css`
    position: relative;
    width: 100%;
    height: 100%;
  `,
  topRightOverlay: css`
    position: absolute;
    top: 10px;
    right: 58px;
    z-index: 10;
  `,
  loadingOverlay: css`
    position: absolute;
    bottom: 12px;
    left: 12px;
    z-index: 10;
    display: flex;
    align-items: center;
    padding: 8px 14px;
    border-radius: 6px;
    background-color: ${theme?.palette?.background?.paper};
    box-shadow: 0px 2px 12px 2px ${theme?.palette?.divider};
  `,
});

export default MoveMapView;
